

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PDFDownloadLink } from '@react-pdf/renderer'
import ApiService from '../../../services/ApiService'

import { X } from 'lucide-react'

import CertificatePDFGenerator from '../CertificatePDFGenerator/PDFTemplate'
import PreviewModal from '../CertificatePDFGenerator/PreviewModal'
import {
 CertificatePDFData,
 ChemicalCompositionRow,
 ChemicalCompositionAdditionalRow,
 MechanicalTestsRow
} from '../CertificatePDFGenerator/types'
import ModalTest from '../../BaseModal'
import { ModalContainer, CloseModal } from './style'

const ViewCertificateModal: React.FC = () => {
 const { id } = useParams()
 const navigate = useNavigate()
 const [certificate, setCertificate] = useState<CertificatePDFData | null>(null)
 const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

 const getFieldValue = (primary: any, ...fallbacks: any[]) => {
   if (typeof primary === 'string' && primary.trim()) return primary
   for (const fallback of fallbacks) {
     if (typeof fallback === 'string' && fallback.trim()) return fallback
   }
   return ''
 }

 useEffect(() => {
   if (!id) {
     console.error('ID do certificado não encontrado na URL.')
     navigate('/panel/certificates')
     return
   }

   Promise.all([
     ApiService.certificates.findById(Number(id)),
     ApiService.certificates.getUtils(Number(id))
   ])
   .then(([response1, response2]: any[]) => {
     const dataFromFindOne = response1.data
     const dataFromUtilsView = response2.data

     const chemicalStructure = dataFromUtilsView.structures?.find((s: any) => s.name === 'Composição química')
     const mechanicalStructure = dataFromUtilsView.structures?.find((s: any) => s.name === 'Ensaios mecânicos')

     const chemicalCompositionValues: ChemicalCompositionRow & ChemicalCompositionAdditionalRow = {
       C: '', Mn: '', Cr: '', Cu: '', Fe: '', Nb: '', Ni: '', P: '', S: '', Si: '', Mo: '',
       Al: '', N: '', W: '', 'V': '', Co: '', Ti: '', B: '', Sn: '', Ta: '', O: '', H: '',
     }

     chemicalStructure?.columns?.forEach((col: any) => {
       if (col.name in chemicalCompositionValues) {
         (chemicalCompositionValues as any)[col.name] = col.value || ''
       }
     })

     const mechanicalTestsValues: MechanicalTestsRow = {
       tensileStrengthValue: '',
       yieldStrengthValue: '',
       elongationValue: '',
       areaReductionValue: '',
       hardnessValue: '',
     }

     mechanicalStructure?.columns?.forEach((col: any) => {
       switch (col.name) {
         case 'Resistência': mechanicalTestsValues.tensileStrengthValue = col.value || ''; break
         case 'Escoamento': mechanicalTestsValues.yieldStrengthValue = col.value || ''; break
         case 'Alongamento': mechanicalTestsValues.elongationValue = col.value || ''; break
         case 'Redução de área': mechanicalTestsValues.areaReductionValue = col.value || ''; break
         case 'Dureza': mechanicalTestsValues.hardnessValue = col.value || ''; break
       }
     })

     const mappedData: CertificatePDFData = {
       id: dataFromFindOne.id || Number(id) || 0,
       customer: getFieldValue(dataFromFindOne.customer?.companyName),
       supplier: getFieldValue(dataFromFindOne.supplier?.companyName),
       date: getFieldValue(dataFromFindOne.date),
       heatNumber: getFieldValue(dataFromFindOne.heatNumber?.heatNumber),
       fiscalNote: getFieldValue(dataFromFindOne.invoiceNumber),
       purchaseOrderNumber: getFieldValue(dataFromFindOne.purchaseOrderNumber),
       norms: getFieldValue(dataFromFindOne.standard),
       manufacturingPlant: getFieldValue(dataFromUtilsView.manufacturingPlant),
       heatTreatmentDetails: getFieldValue(dataFromFindOne.heatTreatment),
       description: getFieldValue(dataFromFindOne.description),
       startingMaterial: getFieldValue(dataFromFindOne.startingMaterial),

       chemicalComposition: {
         headersTop: ["C", "Mn", "Cr", "Cu", "Fe", "Nb", "Ni", "P", "S", "Si", "Mo"],
         valuesTop: {
           C: chemicalCompositionValues.C, Mn: chemicalCompositionValues.Mn, Cr: chemicalCompositionValues.Cr,
           Cu: chemicalCompositionValues.Cu, Fe: chemicalCompositionValues.Fe, Nb: chemicalCompositionValues.Nb,
           Ni: chemicalCompositionValues.Ni, P: chemicalCompositionValues.P, S: chemicalCompositionValues.S,
           Si: chemicalCompositionValues.Si, Mo: chemicalCompositionValues.Mo,
         },
         headersBottom: ["Al", "N", "W", "V", "Co", "Ti", "B", "Sn", "Ta", "O", "H"],
         valuesBottom: {
           Al: chemicalCompositionValues.Al, N: chemicalCompositionValues.N, W: chemicalCompositionValues.W,
           V: chemicalCompositionValues.V, Co: chemicalCompositionValues.Co, Ti: chemicalCompositionValues.Ti,
           B: chemicalCompositionValues.B, Sn: chemicalCompositionValues.Sn, Ta: chemicalCompositionValues.Ta,
           O: chemicalCompositionValues.O, H: chemicalCompositionValues.H,
         },
       },
       mechanicalTests: {
         headers: {
           portuguese: ["Limite de Resistência", "Limite de Escoamento", "Alongamento","Redução de Área", "Dureza"],
           english: ["Tensile Strength", "Yield Strength", "Elongation", "Area Reduction", "Hardness"],
         },
         values: mechanicalTestsValues
       },
       qualityObservations: getFieldValue(dataFromFindOne.observations),
       responsibleName: getFieldValue(dataFromFindOne.responsibleName),
       responsibleSignatureDate: getFieldValue(dataFromFindOne.responsibleSignatureDate),
       additionalPages: dataFromFindOne.images?.sort((a: any, b: any) => a.id - b.id).map((img: any) => ({
         id: img.id,
         title: getFieldValue(img.title),
         subtitle: getFieldValue(img.subTitle),
         imageLink: getFieldValue(img.fileUrl),
       })) || [],
       param: dataFromFindOne.param,
       structures: dataFromUtilsView.structures,
     }

     setCertificate(mappedData)
   })
   .catch((error: any) => {
     console.error('Erro ao carregar certificado:', error)
     alert('Erro ao carregar o certificado. Por favor, tente novamente.')
     navigate('/panel/certificates')
   })
 }, [id, navigate])

 if (!certificate) return <p>Carregando certificado...</p>

 const handleVerPreviewClick = () => {
   setIsPreviewModalOpen(true)
 }

 return (
   <ModalTest>
     <ModalContainer>
       <CloseModal onClick={() => navigate('..')}>
         <X color="#696969" />
       </CloseModal>
       <div
         style={{
           display: 'flex',
           flexDirection: 'column',
           alignItems: 'center',
           justifyContent: 'center',
           height: '100%',
           padding: '2rem',
           textAlign: 'center',
         }}
       >
         <h2 style={{ marginBottom: '1rem', color: '#1e40af' }}>
           Certificado #{certificate.id}
         </h2>
         <p style={{ marginBottom: '2rem', color: '#6b7280' }}>
           Cliente: {certificate.customer} | Nº da Corrida:
           {' '}
           {certificate.heatNumber}
           {certificate.responsibleName &&
             ` | Responsável: ${certificate.responsibleName}`}
         </p>

         {certificate.id !== 0 && (
           <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
             <button
               onClick={handleVerPreviewClick}
               style={{
                 padding: '10px 20px',
                 backgroundColor: '#f0f0f0',
                 color: '#1e40af',
                 borderRadius: '5px',
                 cursor: 'pointer',
                 fontSize: '16px',
                 border: '1px solid #1e40af',
                 display: 'inline-block',
               }}
             >
               Ver Preview
             </button>
             <PDFDownloadLink
               document={<CertificatePDFGenerator certificate={certificate} />}
               fileName={`Certificado_${certificate.id}_${certificate.customer}.pdf`}
               style={{
                 textDecoration: 'none',
                 padding: '10px 20px',
                 backgroundColor: '#1e40af',
                 color: 'white',
                 borderRadius: '5px',
                 cursor: 'pointer',
                 fontSize: '16px',
                 border: 'none',
                 display: 'inline-block',
               }}
             >
               {({ loading }) =>
                 loading ? 'Preparando Certificado...' : 'Baixar Certificado'
               }
             </PDFDownloadLink>
           </div>
         )}
       </div>
       {certificate && (
         <PreviewModal
           certificate={certificate}
           isOpen={isPreviewModalOpen}
           onClose={() => setIsPreviewModalOpen(false)}
         />
       )}
     </ModalContainer>
   </ModalTest>
 )
}

export default ViewCertificateModal
